import { useDemoSafeMutation } from '@/hooks/useDemoGuard';
import { companiesRepository, rolePermissionsRepository } from '@/repositories';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';

export interface CompanySettings {
  id: string;
  name: string;
  name_en: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  cr_number: string | null;
  vat_number: string | null;
  unified_number: string | null;
  logo_url: string | null;
  default_currency: string | null;
  vat_rate: number | null;
  management_fee_type: string | null;
  management_fee_value: number | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_iban: string | null;
  bank_account_holder_name: string | null;
  maintenance_approval_threshold: number | null;
  owner_portal_welcome_message: string | null;
  tenant_portal_welcome_message: string | null;
  settings: Record<string, unknown> | null; // JSONB from DB
}

export interface RolePermission {
  id: string;
  company_id: string;
  role_name: string;
  permissions: string[];
  is_system_role: boolean;
  portal_type: string;
  name_id: number | null;
  created_at: string;
  updated_at: string;
}

// Operation labels for the permission matrix UI
export const PERMISSION_OPERATION_LABELS = {
  view: { ar: 'عرض', en: 'View' },
  create: { ar: 'إنشاء', en: 'Create' },
  edit: { ar: 'تعديل', en: 'Edit' },
  delete: { ar: 'حذف', en: 'Delete' },
} as const;

// Available permission domains with bilingual labels and their granular operations
export const AVAILABLE_PERMISSIONS = [
  {
    key: 'properties',
    label: { ar: 'العقارات', en: 'Properties' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'units',
    label: { ar: 'الوحدات', en: 'Units' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'contracts',
    label: { ar: 'العقود', en: 'Contracts' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'tenants',
    label: { ar: 'المستأجرين', en: 'Tenants' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'owners',
    label: { ar: 'الملاك', en: 'Owners' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'finance',
    label: { ar: 'المالية', en: 'Finance' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'maintenance',
    label: { ar: 'الصيانة', en: 'Maintenance' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'reports',
    label: { ar: 'التقارير', en: 'Reports' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'advertisements',
    label: { ar: 'الإعلانات', en: 'Advertisements' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    // Relabel only (S6): the permission key stays `projects`.
    key: 'projects',
    label: { ar: 'الفرص', en: 'Opportunities' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'employees',
    label: { ar: 'الموظفين', en: 'Employees' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'tasks',
    label: { ar: 'المهام', en: 'Tasks' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'service_requests',
    label: { ar: 'طلبات الخدمة', en: 'Service Requests' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'leasing',
    label: { ar: 'مكتب التأجير', en: 'Leasing desk' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
  {
    key: 'settings',
    label: { ar: 'الإعدادات', en: 'Settings' },
    operations: ['view', 'create', 'edit', 'delete'] as const,
  },
];

// Fetch company settings
export function useCompanySettings() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['company-settings', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      const data = await companiesRepository.getById(companyId);
      return data as CompanySettings | null;
    },
    enabled: !!companyId,
  });
}

// Update company settings
export function useUpdateCompanySettings() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (updates: Partial<CompanySettings>) => {
      if (!companyId) throw new Error('No company ID');
      // The repository owns the settings-Json cast + the
      // "Company not found or access denied" null-row throw.
      return companiesRepository.updateCompany(companyId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    },
  });
}

// Notification preferences moved to a pivoted (user_id, event_key, channel,
// enabled) table by the notification refactor migration. Use the new hooks
// in @/hooks/useNotificationPreferences instead. The old per-category hook
// signatures and the NotificationPreferences interface in @/types/database
// no longer match the schema and have been removed.

// Fetch role permissions
export function useRolePermissions() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['role-permissions', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      const data = await rolePermissionsRepository.listForCompany(companyId);
      return data.map((item) => ({
        ...item,
        permissions: Array.isArray(item.permissions) ? item.permissions : [],
        portal_type: item.portal_type ?? 'employee',
        name_id: item.name_id ?? null,
      })) as RolePermission[];
    },
    enabled: !!companyId,
  });
}

// Create role permission
export function useCreateRolePermission() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (data: {
      role_name: string;
      permissions: string[];
      portal_type?: string;
    }) => {
      if (!companyId) throw new Error('No company ID');

      return rolePermissionsRepository.create(companyId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
      // The set of effective permissions a user holds is derived from
      // their role's `permissions` array — invalidate every user's
      // computed permission cache so the sidebar/page guards pick up
      // the change without a full reload. Cross-session propagation
      // (a different browser) still relies on the Realtime subscription
      // in usePermissions.ts.
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
    },
  });
}

// Update role permission
export function useUpdateRolePermission() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (data: {
      id: string;
      permissions: string[];
      portal_type?: string;
    }) => {
      if (!companyId) throw new Error('No company ID');

      return rolePermissionsRepository.update(companyId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
      // The set of effective permissions a user holds is derived from
      // their role's `permissions` array — invalidate every user's
      // computed permission cache so the sidebar/page guards pick up
      // the change without a full reload. Cross-session propagation
      // (a different browser) still relies on the Realtime subscription
      // in usePermissions.ts.
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
    },
  });
}

// Delete role permission
export function useDeleteRolePermission() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error('No company ID');

      await rolePermissionsRepository.remove(id, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
      // The set of effective permissions a user holds is derived from
      // their role's `permissions` array — invalidate every user's
      // computed permission cache so the sidebar/page guards pick up
      // the change without a full reload. Cross-session propagation
      // (a different browser) still relies on the Realtime subscription
      // in usePermissions.ts.
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
    },
  });
}

// Clone a role — creates a copy with a new name and is_system_role=false
export function useCloneRolePermission() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (data: {
      sourceRole: RolePermission;
      newName: string;
    }) => {
      if (!companyId) throw new Error('No company ID');

      return rolePermissionsRepository.clone(
        companyId,
        data.sourceRole,
        data.newName,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
      // The set of effective permissions a user holds is derived from
      // their role's `permissions` array — invalidate every user's
      // computed permission cache so the sidebar/page guards pick up
      // the change without a full reload. Cross-session propagation
      // (a different browser) still relies on the Realtime subscription
      // in usePermissions.ts.
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
    },
  });
}

// Count how many user_role_assignments reference a given role (for delete confirmation)
export function useRoleAssignmentCount(roleId: string | null) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ['role-assignment-count', companyId, roleId],
    queryFn: async () => {
      if (!companyId || !roleId) return 0;

      return rolePermissionsRepository.countAssignments(roleId, companyId);
    },
    enabled: !!companyId && !!roleId,
    staleTime: 30 * 1000,
  });
}
